import { useState, useEffect, useRef } from "react";
import { flushSync, createPortal } from "react-dom";
import { apiFetch } from "./api";
import { ymd, formatEventDateShort as formatEventDate, formatTime12 as formatTime } from "./utils";
import "./AvailabilityModal.css";

export default function AvailabilityModal({ user, userType = "athlete", isOpen, onClose, onNavigateToCalendar, practiceId = null }) {
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [optimisticStatus, setOptimisticStatus] = useState({}); // Track optimistic updates
  const [responseInputs, setResponseInputs] = useState({});
  const [responseErrors, setResponseErrors] = useState({});
  const [expandedPracticeId, setExpandedPracticeId] = useState(null);
  const [pendingAvailable, setPendingAvailable] = useState({});
  const autosaveTimers = useRef({});

  useEffect(() => {
    if (!isOpen || !user?.id) return;

    const MIN_PRACTICES = 7;
    const INITIAL_WINDOW_DAYS = 7;
    const MAX_WINDOW_DAYS = 84; // stop widening after ~12 weeks

    async function fetchPracticesAndAttendance(from, to) {
      const params = new URLSearchParams({ from, to });
      // Don't filter by team — the backend already scopes by the athlete's squad,
      // and team name mismatches (casing, etc.) can hide valid practices

      // Determine which endpoint to use based on user type
      const attendanceEndpoint = userType === "coach"
        ? `/attendance/coach/range?user=${user.id}&from=${from}&to=${to}`
        : `/attendance/range?user=${user.id}&from=${from}&to=${to}`;

      const [practiceRows, attendanceRows] = await Promise.all([
        apiFetch(`/practices/range?${params.toString()}`),
        apiFetch(attendanceEndpoint)
      ]);

      return {
        practicesList: Array.isArray(practiceRows) ? practiceRows : [],
        attendance: Array.isArray(attendanceRows) ? attendanceRows : [],
      };
    }

    async function loadPractices() {
      setLoading(true);
      try {
        const today = new Date();

        // Start with a 7-day window, then keep widening it until we have at
        // least MIN_PRACTICES upcoming practices (or we hit the max window).
        // When a specific practiceId is requested, keep widening until that
        // practice is found instead — it may be well beyond 7 days out.
        let windowDays = INITIAL_WINDOW_DAYS;
        let practicesList = [];
        let attendance = [];
        const todayStr = ymd(today);

        while (true) {
          const endDate = new Date(today);
          endDate.setDate(today.getDate() + windowDays);
          const from = todayStr;
          const to = ymd(endDate);

          const result = await fetchPracticesAndAttendance(from, to);
          practicesList = result.practicesList;
          attendance = result.attendance;

          if (windowDays >= MAX_WINDOW_DAYS) break;

          if (practiceId) {
            const found = practicesList.some(p => p.id === practiceId);
            if (found) break;
          } else {
            const upcomingCount = practicesList.filter(p => p.state !== "off" && p.date && p.date.slice(0, 10) >= todayStr).length;
            if (upcomingCount >= MIN_PRACTICES) break;
          }
          windowDays *= 2;
        }

        console.log('[AvailabilityModal] raw practiceRows:', JSON.stringify(practicesList?.map(p => ({ id: p.id, date: p.date, startTime: p.startTime || p.start_time || p.time, state: p.state, name: p.name }))));

        // Create attendance lookup by practice ID
        const attendanceMap = new Map(
          attendance.map(a => [
            a.practice_id,
            { status: a.status || null, responseData: a.responseData || null },
          ])
        );

        // Filter and normalize practices — include anything on today or later
        // (don't filter by start time so practices earlier today aren't dropped)
        const upcomingPractices = practicesList
          .filter(p => {
            if (practiceId) return p.id === practiceId;
            if (p.state === "off") return false;
            const passes = p.date && p.date.slice(0, 10) >= todayStr;
            if (!passes) console.log('[AvailabilityModal] filtered out:', p.id, p.date, p.state);
            return passes;
          })
          .map(p => {
            const attendanceEntry = attendanceMap.get(p.id) || {};
            return {
              id: p.id,
              date: p.date,
              startTime: p.startTime || p.start_time || p.time,
              endTime: p.endTime || p.end_time,
              name: p.name || "Practice",
              location: p.location,
              squad: p.squad || p.squadName,
              note: p.note,
              responseFields: Array.isArray(p.responseFields) ? p.responseFields : [],
              responseData: attendanceEntry.responseData || null,
              status: attendanceEntry.status || null,
            };
          })
          .sort((a, b) => {
            const dateA = new Date(`${a.date}T${a.startTime || "00:00"}`);
            const dateB = new Date(`${b.date}T${b.startTime || "00:00"}`);
            return dateA - dateB;
          });

        // Cap at the next 7 practices (the fetch window may have been widened
        // beyond 7 days to find enough practices, or may contain more than 7
        // within the initial 7-day window).
        const finalPractices = practiceId ? upcomingPractices : upcomingPractices.slice(0, MIN_PRACTICES);

        console.log('[AvailabilityModal] final upcomingPractices:', JSON.stringify(finalPractices.map(p => ({ id: p.id, date: p.date, startTime: p.startTime, name: p.name }))));
        setPractices(finalPractices);
        setResponseInputs(prev => {
          const next = { ...prev };
          finalPractices.forEach((practice) => {
            if (!next[practice.id]) {
              if (practice.responseData && typeof practice.responseData === "object") {
                next[practice.id] = { ...practice.responseData };
              } else {
                next[practice.id] = {};
              }
            }
          });
          return next;
        });
      } catch (err) {
        console.error("Failed to load practices:", err);
      } finally {
        setLoading(false);
      }
    }

    loadPractices();
  }, [isOpen, user, userType, practiceId]);

  async function updateStatus(practiceId, newStatus, responses = null) {
    if (!user?.id) return;

    console.log('[AvailabilityModal] Setting optimistic status:', practiceId, newStatus);

    // Set optimistic status immediately - this is the key for iOS
    flushSync(() => {
      setOptimisticStatus(prev => {
        const updated = { ...prev, [practiceId]: newStatus };
        console.log('[AvailabilityModal] Optimistic status updated:', updated);
        return updated;
      });
      setSaving(prev => ({ ...prev, [practiceId]: true }));
    });

    // AGGRESSIVE iOS Safari repaint hack
    if (typeof window !== "undefined") {
      // Method 1: Force layout recalculation
      void document.body.offsetHeight;

      // Method 2: Toggle a class on body
      document.body.classList.add('force-repaint');
      requestAnimationFrame(() => {
        document.body.classList.remove('force-repaint');
      });
    }

    try {
      // Use appropriate endpoint based on user type
      const endpoint = userType === "coach"
        ? `/attendance/coach/mine?as=${user.id}`
        : `/attendance/mine?as=${user.id}`;

      await apiFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({
          status: newStatus,
          practice_id: practiceId,
          responses: responses || null,
        })
      });

      // Update actual practices state after successful API call
      setPractices(prev =>
        prev.map(p => (p.id === practiceId ? { ...p, status: newStatus } : p))
      );
      setPendingAvailable(prev => {
        const next = { ...prev };
        delete next[practiceId];
        return next;
      });
    } catch (err) {
      console.error("Failed to update availability:", err);
      alert(err?.message || "Failed to update availability");
      // Clear optimistic status on error
      setOptimisticStatus(prev => {
        const next = { ...prev };
        delete next[practiceId];
        return next;
      });
    } finally {
      setSaving(prev => {
        const next = { ...prev };
        delete next[practiceId];
        return next;
      });
      // Clear optimistic status after save completes
      setTimeout(() => {
        setOptimisticStatus(prev => {
          const next = { ...prev };
          delete next[practiceId];
          return next;
        });
      }, 100);
    }
  }

  function validateResponses(practiceId, responseFields) {
    const inputs = responseInputs[practiceId] || {};
    const missing = {};
    (responseFields || []).forEach((field) => {
      const value = inputs[field.id];
      if (value == null || String(value).trim() === "") {
        missing[field.id] = true;
      }
    });
    return missing;
  }

  function validateResponsesForInputs(inputs, responseFields) {
    const missing = {};
    (responseFields || []).forEach((field) => {
      const value = inputs?.[field.id];
      if (value == null || String(value).trim() === "") {
        missing[field.id] = true;
      }
    });
    return missing;
  }

  function handleStatusClick(practiceId, currentStatus, targetStatus, responseFields) {
    const newStatus = currentStatus === targetStatus ? null : targetStatus;
    if (newStatus === "Available" && (responseFields || []).length > 0) {
      const missing = validateResponses(practiceId, responseFields);
      if (Object.keys(missing).length > 0) {
        setExpandedPracticeId(practiceId);
        setResponseErrors((prev) => ({ ...prev, [practiceId]: missing }));
        setPendingAvailable((prev) => ({ ...prev, [practiceId]: true }));
        return;
      }
      setResponseErrors((prev) => ({ ...prev, [practiceId]: {} }));
      updateStatus(practiceId, newStatus, responseInputs[practiceId] || {});
      return;
    }
    if (newStatus !== "Available") {
      setResponseErrors((prev) => ({ ...prev, [practiceId]: {} }));
      setPendingAvailable((prev) => {
        const next = { ...prev };
        delete next[practiceId];
        return next;
      });
    }
    updateStatus(practiceId, newStatus);
  }

  function handleViewFullSchedule() {
    if (onNavigateToCalendar) {
      onNavigateToCalendar();
      onClose();
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <div className="modalOverlay" onClick={onClose}>
      <div className="modalCard availability-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          aria-label="Close availability modal"
          onClick={onClose}
        >
          &times;
        </button>

        <div className="availability-modal-header">
          <h3 className="h2 availability-modal-title">
            {practiceId ? "RSVP for This Practice" : "My Availability - Next 7 Practices"}
          </h3>
          <p className="help">
            {practiceId
              ? "Mark your availability for this practice"
              : "Mark your availability for upcoming practices"}
          </p>
        </div>

        <div className="availability-modal-body">
          {loading ? (
            <div className="help" style={{ textAlign: "center", padding: "2rem" }}>
              Loading practices...
            </div>
          ) : practices.length === 0 ? (
            <div className="help" style={{ textAlign: "center", padding: "2rem" }}>
              No upcoming practices scheduled
            </div>
          ) : (
            <div className="availability-practices-list">
              {practices.map((practice) => {
                const displayStatus = optimisticStatus[practice.id] !== undefined ? optimisticStatus[practice.id] : practice.status;
                console.log('[AvailabilityModal] Rendering practice', practice.id, 'status:', practice.status, 'optimistic:', optimisticStatus[practice.id], 'display:', displayStatus);
                const isAvailable = displayStatus === "Available";
                const isNotAvailable = displayStatus === "Not Available";
                const hasResponseFields = (practice.responseFields || []).length > 0;
                const showResponseFields = hasResponseFields && (isAvailable || expandedPracticeId === practice.id);
                return (
                <div key={`${practice.id}-${displayStatus || 'none'}`} className="availability-practice-item">
                  <div className="availability-practice-info">
                    <div className="availability-practice-title">
                      {practice.name}
                    </div>
                    <div className="availability-practice-details">
                      <div className="availability-practice-detail">
                        📅 {formatEventDate(practice.date)}
                      </div>
                      <div className="availability-practice-detail">
                        ⏰ {formatTime(practice.startTime)}
                        {practice.endTime && ` – ${formatTime(practice.endTime)}`}
                      </div>
                      {practice.location && (
                        <div className="availability-practice-detail">
                          📍 {practice.location}
                        </div>
                      )}
                      {practice.squad && (
                        <div className="availability-practice-detail">
                          👥 {practice.squad}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="availability-practice-actions">
                    <button
                      key={`avail-${practice.id}-${displayStatus || 'none'}`}
                      ref={(el) => {
                        if (el && isAvailable) {
                          // Force iOS Safari to repaint by toggling a style
                          el.style.opacity = '0.99';
                          requestAnimationFrame(() => {
                            el.style.opacity = '';
                          });
                        }
                      }}
                      className={`availability-btn availability-btn-available ${
                        isAvailable ? "active" : ""
                      }`}
                      data-active={isAvailable ? "true" : "false"}
                      aria-pressed={isAvailable}
                      style={isAvailable ? { background: "#16a34a", color: "white", borderColor: "#16a34a" } : undefined}
                      onClick={() =>
                        handleStatusClick(practice.id, displayStatus, "Available", practice.responseFields)
                      }
                      disabled={saving[practice.id]}
                      aria-label={
                        isAvailable
                          ? "Clear availability"
                          : "Mark as available"
                      }
                    >
                      ✓ Available
                    </button>
                    <button
                      key={`unavail-${practice.id}-${displayStatus || 'none'}`}
                      ref={(el) => {
                        if (el && isNotAvailable) {
                          // Force iOS Safari to repaint by toggling a style
                          el.style.opacity = '0.99';
                          requestAnimationFrame(() => {
                            el.style.opacity = '';
                          });
                        }
                      }}
                      className={`availability-btn availability-btn-unavailable ${
                        isNotAvailable ? "active" : ""
                      }`}
                      data-active={isNotAvailable ? "true" : "false"}
                      aria-pressed={isNotAvailable}
                      style={isNotAvailable ? { background: "#dc2626", color: "white", borderColor: "#dc2626" } : undefined}
                      onClick={() =>
                        handleStatusClick(practice.id, displayStatus, "Not Available", practice.responseFields)
                      }
                      disabled={saving[practice.id]}
                      aria-label={
                        isNotAvailable
                          ? "Clear availability"
                          : "Mark as unavailable"
                      }
                    >
                      ✗ Not Available
                    </button>
                  </div>
                  {showResponseFields && (
                    <div className="stack" style={{ marginTop: 10, gap: 8 }}>
                      {(practice.responseFields || []).map((field) => {
                        const fieldType = String(field.type || "text").toLowerCase();
                        const fieldValue = responseInputs[practice.id]?.[field.id] ?? "";
                        const hasError = responseErrors?.[practice.id]?.[field.id];
                        const displayStatus = optimisticStatus[practice.id] !== undefined
                          ? optimisticStatus[practice.id]
                          : practice.status;
                        return (
                          <label key={field.id} className="stack" style={{ gap: 4 }}>
                            <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>
                              {field.label} {hasError ? "(required)" : ""}
                            </span>
                            {fieldType === "picklist" ? (
                              <select
                                className="input"
                                value={fieldValue}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const nextInputs = {
                                    ...(responseInputs[practice.id] || {}),
                                    [field.id]: value,
                                  };
                                  setResponseInputs((prev) => ({
                                    ...prev,
                                    [practice.id]: nextInputs,
                                  }));
                                  setResponseErrors((prev) => ({
                                    ...prev,
                                    [practice.id]: { ...(prev[practice.id] || {}), [field.id]: false },
                                  }));
                                  if (pendingAvailable[practice.id]) {
                                    const missing = validateResponsesForInputs(nextInputs, practice.responseFields);
                                    if (Object.keys(missing).length === 0) {
                                      updateStatus(practice.id, "Available", nextInputs);
                                    }
                                  } else if (displayStatus === "Available") {
                                    const timerKey = practice.id;
                                    if (autosaveTimers.current[timerKey]) {
                                      clearTimeout(autosaveTimers.current[timerKey]);
                                    }
                                    autosaveTimers.current[timerKey] = setTimeout(() => {
                                      updateStatus(practice.id, "Available", nextInputs);
                                    }, 400);
                                  }
                                }}
                              >
                                <option value="">Select...</option>
                                {(field.options || []).map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                className="input"
                                type="text"
                                value={fieldValue}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const nextInputs = {
                                    ...(responseInputs[practice.id] || {}),
                                    [field.id]: value,
                                  };
                                  setResponseInputs((prev) => ({
                                    ...prev,
                                    [practice.id]: nextInputs,
                                  }));
                                  setResponseErrors((prev) => ({
                                    ...prev,
                                    [practice.id]: { ...(prev[practice.id] || {}), [field.id]: false },
                                  }));
                                  if (pendingAvailable[practice.id]) {
                                    const missing = validateResponsesForInputs(nextInputs, practice.responseFields);
                                    if (Object.keys(missing).length === 0) {
                                      updateStatus(practice.id, "Available", nextInputs);
                                    }
                                  } else if (displayStatus === "Available") {
                                    const timerKey = practice.id;
                                    if (autosaveTimers.current[timerKey]) {
                                      clearTimeout(autosaveTimers.current[timerKey]);
                                    }
                                    autosaveTimers.current[timerKey] = setTimeout(() => {
                                      updateStatus(practice.id, "Available", nextInputs);
                                    }, 400);
                                  }
                                }}
                              />
                            )}
                          </label>
                        );
                      })}
                      <div className="row" style={{ gap: 8, justifyContent: "flex-end" }} />
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="availability-modal-footer">
          {onNavigateToCalendar && !practiceId && (
            <button className="btn ghost" onClick={handleViewFullSchedule}>
              📅 View Full Schedule
            </button>
          )}
          <button className="btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

